import type { SmartSortProduct } from '@iheartjane/dm-sdk';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { AlgoliaProduct } from '@jane/search/data-access';

import { useSDK } from '../sdkProvider';

interface UseFetchProduct<ProductShape extends object = AlgoliaProduct> {
  isError: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  product: SmartSortProduct<ProductShape> | undefined;
}

type UseFetchProductProps = {
  enabled?: boolean;
  productId: number;
  searchAttributes?: string[];
  storeId: number;
};

export const useFetchProduct = <ProductShape extends object = AlgoliaProduct>(
  props: UseFetchProductProps
): UseFetchProduct<ProductShape> => {
  const {
    enabled = true,
    searchAttributes = ['*'],
    storeId,
    productId,
  } = props;

  const { sdk, isReady } = useSDK();

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [product, setProduct] = useState<
    SmartSortProduct<ProductShape> | undefined
  >(undefined);

  const fetchProduct = useCallback(async () => {
    if (!sdk || !isReady) {
      return;
    }

    setIsLoading(true);
    setIsError(false);
    setIsSuccess(false);
    setProduct(undefined);

    try {
      if (!enabled) {
        setIsSuccess(true);
        return;
      }

      const response = await sdk.fetchProduct<ProductShape>({
        productId,
        storeId,
        searchAttributes,
      });
      setProduct(response);
      setIsSuccess(true);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, sdk, isReady, storeId, productId]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  const response = useMemo(
    () => ({
      isError,
      isLoading,
      isSuccess,
      product,
    }),
    [isError, isLoading, isSuccess, product]
  );

  return response;
};
